import { __, sprintf } from "@wordpress/i18n";
import { useState, useEffect, Fragment } from "@wordpress/element";
import { TextareaControl, ComboboxControl, RadioControl, Spinner, ToolbarGroup, ToolbarButton } from "@wordpress/components";
import apiFetch from '@wordpress/api-fetch';
import { addQueryArgs } from '@wordpress/url';
import { BlockControls, InnerBlocks } from "@wordpress/block-editor";
import { useSelect } from "@wordpress/data";
import { serialize } from "@wordpress/blocks";
import useDebounce from './use-debounce';
import { LinkGeniusTooltip } from "./linkgenius-tooltip";
const ALLOWED_PLACEHOLDERS = linkgenius_editor_data['allowed_placeholders'];

function LinkGeniusTaxonomySelector(props) {
    if (props === undefined) return;

    const { clientId, attributes, setAttributes, taxonomy } = props;
    const [taxonomyItems, setTaxonomyItems] = useState(null);
    const [previewHTML, setPreviewHTML] = useState(null);
    const [isPreviewMode, setPreviewMode] = useState(attributes.item_slug !== undefined && attributes.item_slug != "");

    const {blockContent, allBlocks} = useSelect((select) => {
        
        const { getBlock } = select('core/block-editor');
        const block = getBlock(clientId);
        const getDescendantIds = (block) => {
            return block.innerBlocks.reduce((acc, innerBlock) => {
                return acc.concat(getDescendantIds(innerBlock));
            }, [block.clientId]);
        };
        return {blockContent: serialize(block?.innerBlocks), allBlocks: getDescendantIds(block)};
    }, allBlocks ?? [clientId]);
    useEffect(() => {
        const template = attributes.template_type == 'manual' ? attributes.template : blockContent;
        setPreviewHTML("");
        getPreview(taxonomy, attributes.item_slug, attributes.sort, template, attributes.template_type).then((preview) => {
            setPreviewHTML(preview);
        });
    }, [attributes.item_slug, attributes.sort, attributes.template, attributes.template_type, taxonomy, useDebounce(blockContent, 3000)]);

    useEffect(() => {
        if (taxonomyItems === null) {
            if (!fetchers[taxonomy]) {
                fetchers[taxonomy] = new TaxonomyFetcher(taxonomy);
            }
            fetchers[taxonomy].subscribers.push(setTaxonomyItems);
            fetchers[taxonomy].promise.then(() => {
                setTaxonomyItems(fetchers[taxonomy].options);
            });
        }
    }, [taxonomy, taxonomyItems]);

    const TEMPLATE = [
        ['core/list', {}, [
            ['linkgenius/linkloop', {}, [
                ['core/list-item', { content: '{{lg_link}}' }]
            ]]
        ]]
    ];

    return (
        <Fragment>
            <BlockControls>
                <ToolbarGroup label={__('LinkGenius List Settings')}>
                    <ToolbarButton
                        label={isPreviewMode ? __('Edit LinkGenius List', 'linkgenius') : __('Preview LinkGenius List', 'linkgenius')}
                        icon={isPreviewMode ? "edit" : "visibility"}
                        onClick={() => setPreviewMode(!isPreviewMode)}
                    />
                </ToolbarGroup>
            </BlockControls>
            {isPreviewMode ? (
                <div>
                    {previewHTML === "" ? <div className="linkgenius-taxonomy-selector"><Spinner /></div> : <div className="linkgenius-list-preview" dangerouslySetInnerHTML={{ __html: previewHTML }}></div>}
                    <div style={{ display: 'none' }}><InnerBlocks template={TEMPLATE} /></div>
                </div>
            ) : (
                <div className="linkgenius-taxonomy-selector">
                    <h2>{taxonomy == 'category' ? __('LinkGenius Category List', 'linkgenius') : __('LinkGenius Tag List', 'linkgenius')}</h2>
                    <div>
                        {taxonomy == 'category' ? __('Category', 'linkgenius') : __('Tag', 'linkgenius')}:
                        <ComboboxControl
                            value={attributes.item_slug ?? ""}
                            options={taxonomyItems ?? []}
                            onChange={(newValue) => setAttributes({ item_slug: newValue })}
                        />
                    </div>
                    <div className="linkgenius-list-sort">
                        {__('Sort By', 'linkgenius')}:
                        <RadioControl options={[
                            { label: __('Order', 'linkgenius'), value: 'order' },
                            { label: __('Title', 'linkgenius'), value: 'title' },
                        ]} selected={attributes.sort ?? 'order'} onChange={(newValue) => setAttributes({ sort: newValue })} />
                    </div>
                    <div className="linkgenius-list-template-type">
                        {__('Template', 'linkgenius')}:
                        <LinkGeniusTooltip text={__('Manual template allows you to write own html or specify a separator. Blocks template allow you to specify repeated blocks through the LinkGenius Link Loop.', 'linkgenius')} />    
                        <RadioControl options={[
                            { label: __('Manual', 'linkgenius'), value: 'manual' },
                            { label: __('Blocks', 'linkgenius'), value: 'blocks' },
                        ]} selected={attributes.template_type ?? 'template'} onChange={(newValue) => setAttributes({ template_type: newValue })} />
                        
                    </div>
                    {attributes.template_type == 'manual' 
                        ? <div>
                            {__('Template or separator', 'linkgenius')}:
                            <LinkGeniusTooltip text={sprintf(__('Use {links}{/links} to specify html of each link. {link} will display the link in default layout, other placeholders that can be used are: %s.\r\n\r\nIf {links}.*{/links} is not found value is treated as separator. For example, Set to ", " to make links comma separated', 'linkgenius'),
                                ALLOWED_PLACEHOLDERS.map(a => "{{lg_"+a.value+"}}").join(", "))} />
                            <TextareaControl rows={5} onChange={(newtext) => setAttributes({ template: newtext })} value={attributes.template ?? ""}></TextareaControl>
                        </div> 
                        : <InnerBlocks template={TEMPLATE} />}
                </div>
            )}
        </Fragment>
    );
}

export default LinkGeniusTaxonomySelector;

const fetchers = [];
class TaxonomyFetcher {
    subscribers = [];
    options = [];
    promise = null;
    constructor(taxonomy) {
        const fetchData = async () => {
            const per_page = 100;
            let total = [];
            for (let page = 1; page < 100; page++) {
                const url = wpApiSettings.root + "wp/v2/linkgenius_" + taxonomy + (wpApiSettings.root.includes("?") ? "&" : "?") + "per_page=" + per_page + "&page=" + page;
                const response = await fetch(url);
                if (response.status !== 200) break;
                const data = await response.json();
                total = total.concat(Array.isArray(data) ? data.map((o) => { return { value: o.slug, label: o.name }; }) : []);
                this.options = total;
                this.subscribers.forEach((subscriber) => {
                    subscriber(this.options);
                });
                if (data.length < per_page) break;
            }
        };
        this.promise = fetchData();
    }
}

async function getPreview(taxonomy, item_slug, sort, template, template_type) {
    const err = (message) => `<span class="linkgenius-error">${message}</span>`;
    if(!item_slug || item_slug == "") {
        return err(sprintf(__('No LinkGenius %s specified', 'linkgenius'), taxonomy));
    }
    const response = await apiFetch({
        url: addQueryArgs(ajaxurl, {
            action: 'preview_linkgenius_taxonomy',
            taxonomy: taxonomy,
            item_slug: item_slug,
            sort: sort,
            template: template,
            template_type: template_type,
        })
    });
    if (response.success) {
        const preview = response.data;
        // Parse the HTML to check for link tags
        var hasContent = preview.startsWith("<!--");
        if(!hasContent) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(preview, 'text/html');
            hasContent = doc.querySelector('a') !== null;
        }

        return hasContent ? preview : err(__('No LinkGenius links found', 'linkgenius'));
    } else {
        return err(__('Error fetching preview', 'linkgenius'));
    }
}
